import React, { useState } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  TextField,
  Paper
} from "@mui/material";
import { Visibility, VisibilityOff, Login } from "@mui/icons-material";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import StyledButton from "./StyledButton";

const LoginForm = ({ formData, handleChange, handleNext, loading = false, errorMessage = '' }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ username: '', password: '' });

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Clear validation errors when user starts typing
  const handleInputChange = (e) => {
    const { name } = e.target;
    if (errors[name === 'email' ? 'username' : name]) {
      setErrors(prev => ({ ...prev, [name === 'email' ? 'username' : name]: '' }));
    }
    handleChange(e);
  };

  const validate = () => {
    let newErrors = { username: '', password: '' };
    let isValid = true;

    if (!formData.email || formData.email.trim() === '') {
      newErrors.username = 'Username is required';
      isValid = false;
    } else if (formData.email.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const onSubmit = (e) => {
    e?.preventDefault();
    if (validate()) {
      handleNext(); // Only proceed if inputs are valid
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSubmit(e);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 450 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AccountCircleIcon sx={{ color: 'primary.main', fontSize: 36 }} />
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{
            color: 'primary.main',
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            letterSpacing: '-0.02em'
          }}
        >
          Login to Your Account
        </Typography>
      </Box>

      <TextField
        fullWidth
        name="email"
        label="Username"
        variant="outlined"
        margin="normal"
        type="text"
        size="medium"
        value={formData.email}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        error={!!errors.username}
        helperText={errors.username}
        sx={{
          borderRadius: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          }
        }}
        autoComplete="username"
      />

      <FormControl fullWidth margin="normal" variant="outlined" size="medium">
        <InputLabel htmlFor="password" error={!!errors.password}>Password</InputLabel>
        <OutlinedInput
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          label="Password"
          error={!!errors.password}
          autoComplete="current-password"
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={togglePasswordVisibility}
                edge="end"
                aria-label="toggle password visibility"
                sx={{
                  color: 'text.secondary',
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.12)'
                      : 'rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          sx={{
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
        {errors.password && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {errors.password}
          </Typography>
        )}
      </FormControl>

      <Box sx={{ mt: 4 }}>
        <StyledButton
          onClick={onSubmit}
          text="Login"
          loading={loading}
          startIcon={<Login />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            fontSize: '1rem'
          }}
        />
      </Box>

      {errorMessage && (
        <Typography
          variant="body2"
          color="error"
          align="center"
          sx={{
            mt: 3,
            fontSize: '0.875rem',
            fontWeight: 500,
            p: 2,
            borderRadius: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(244, 67, 54, 0.1)'
              : 'rgba(244, 67, 54, 0.05)',
            border: '1px solid',
            borderColor: 'error.main'
          }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default LoginForm;
